import type { Device, ProvisioningPreview } from '@arcnet/contracts';
import { Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * The parts of provisioning that two screens both need.
 *
 * The account-wide Provisioning page answers "which handsets can fetch a
 * configuration right now"; the tab on an extension answers the same question
 * for the one phone in front of you. They are different questions at different
 * scales, but the ANSWER has to be identical -- a window that reads "open · 20
 * min left" on one screen and "shut" on the other is worse than either.
 *
 * So the vocabulary lives here: what a vendor is called, what the three window
 * states are, and what a generated file looks like. Extracted when the extension
 * tab was built rather than copied, because the copy is how they drift.
 */

export const VENDOR_LABEL: Record<string, string> = {
  yealink: 'Yealink', fanvil: 'Fanvil', grandstream: 'Grandstream',
  polycom: 'Poly', snom: 'Snom', cisco: 'Cisco', generic: 'Other',
};


/**
 * What to type into a phone's Auto Provision page (owner, 26 September 2026): the URL and the
 * handset's own login. Every fetch -- the first, a re-provision, after a factory reset -- needs it;
 * there is no window. Held only while shown, like a revealed SIP password.
 */
export function LoginPanel({ login, onClose }: {
  login: { url: string | null; username: string; password: string }; onClose: () => void;
}) {
  const copy = (v: string) => { void navigator.clipboard?.writeText(v).catch(() => undefined); };
  const row = (label: string, value: string) => (
    <>
      <dt>{label}</dt>
      <dd className="arc-row" style={{ gap: 6 }}>
        <code className="arc-mono" style={{ fontSize: 12.5, userSelect: 'all' }}>{value}</code>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => copy(value)}>Copy</button>
      </dd>
    </>
  );
  return (
    <div className="arc-stack" style={{ gap: 8 }}>
      <div className="arc-row" style={{ gap: 8 }}>
        <strong style={{ fontSize: 13 }}>Provisioning login</strong>
        <span className="arc-subtle" style={{ fontSize: 12 }}>On the phone: Settings, Auto Provision. Then Auto Provision Now.</span>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onClose}>Hide</button>
      </div>
      <dl className="arc-kv" style={{ margin: 0 }}>
        {row('Server URL', login.url ?? 'Ask your provider for the provisioning address')}
        {row('User name', login.username)}
        {row('Password', login.password)}
      </dl>
    </div>
  );
}

/** The file itself, as the phone will receive it. */
export function Preview({ data, canReveal, onReveal }: {
  data: ProvisioningPreview; canReveal: boolean; onReveal: () => void;
}) {
  return (
    <div className="arc-stack" style={{ gap: 10 }}>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span>{data.generates_config ? `${data.vendor_label} configuration` : 'settings to enter by hand'}</span>
        {data.skipped_lines > 0 && (
          <Badge tone="warn">
            {data.skipped_lines} line{data.skipped_lines === 1 ? '' : 's'} left out — no recoverable password
          </Badge>
        )}
        {data.template && <span className="arc-subtle">{data.template.name} · v{data.template.version}</span>}
        <div className="arc-spacer" />
        {!data.password_shown && canReveal && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={onReveal}>Show password</button>
        )}
      </div>

      {/* Keys its template asks for that this phone cannot have: said, never guessed. */}
      {data.warnings.length > 0 && (
        <div className="arc-banner arc-banner-warn">
          <Icon name="warning" size={14} />
          <div>{data.warnings.map((w) => <div key={w}>{w}</div>)}</div>
        </div>
      )}

      {data.files.map((f) => (
        <div key={f.name}>
          <div className="arc-row arc-subtle" style={{ fontSize: 11.5, marginBottom: 4 }}>
            <span className="arc-mono">{f.name}</span>
            <div className="arc-spacer" />
            <span>{f.content_type}</span>
          </div>
          <pre className="arc-mono" style={{
            margin: 0, padding: 10, fontSize: 11.5, lineHeight: 1.5,
            background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)',
            maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>{f.body}</pre>
        </div>
      ))}

      <span className="arc-subtle" style={{ fontSize: 11.5 }}>
        Generated by the same code that answers the phone.
        {data.password_shown ? ' This copy contains the SIP password.' : ' The password is hidden.'}
      </span>
    </div>
  );
}
